// src/components/BrowserTerminal.js

import React, { useState, useEffect } from 'react';
import { Paper, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactConsoleEmulator from 'react-console-emulator';


export default function BrowserTerminal({ name, onClose, files = [] }) {
  const [pyodide, setPyodide] = useState(null);
  const [pyodideLoading, setPyodideLoading] = useState(true);

  useEffect(() => {
    async function loadPyodideAndPackages() {
      try {
        setPyodideLoading(true);

        // "loadPyodide" is provided by the script in public/index.html
        const pyodideInstance = await window.loadPyodide({
          indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.22.1/full/',
        });

        // Load micropip so we can install additional packages
        await pyodideInstance.loadPackage('micropip');

        setPyodide(pyodideInstance);
        setPyodideLoading(false);
        console.log('Pyodide loaded successfully.');
      } catch (error) {
        console.error('Error loading Pyodide:', error);
      }
    }

    loadPyodideAndPackages();
  }, []);

  /**
   * Helper function: copy all provided files into Pyodide's virtual file system.
   */
  async function copyFilesToPyodideFS() {
    if (!pyodide) return;
    for (const file of files) {
      const code = await file.text();
      try {
        pyodide.FS.writeFile(`/${file.name}`, code);
      } catch (err) {
        console.warn(`FS write error: ${err}`);
      }
    }
  }

  /**
   * Our commands for react-console-emulator.
   */
  const commands = {
    // 1. Python command to run .py files
    python: {
      description: 'Run a Python file (e.g., "python script.py").',
      usage: 'python <filename.py>',
      fn: async (filename) => {
        if (!filename) {
          return 'Usage: python <filename.py>';
        }
        if (!filename.endsWith('.py')) {
          return `Error: "${filename}" is not a .py file.`;
        }

        // If Pyodide not yet loaded
        if (pyodideLoading || !pyodide) {
          return 'Pyodide is still loading. Please wait a moment.';
        }

        // Check if the file exists in our list
        const file = files.find((f) => f.name === filename);
        if (!file) {
          return `Error: File "${filename}" not found.`;
        }

        try {
          // Copy all files into Pyodide FS so local imports work
          await copyFilesToPyodideFS();

          // Make sure Python sees the root directory, then redirect stdout
          pyodide.runPython(`
import sys
import io

# Ensure the root directory is on sys.path
if "/" not in sys.path:
    sys.path.insert(0, "/")

# Redirect stdout to capture output
sys.stdout = io.StringIO()
          `);

          // Run the file directly from the FS
          pyodide.runPython(`exec(open("/${filename}").read())`);

          // Get any printed output from stdout
          const stdout = pyodide.runPython('sys.stdout.getvalue()');

          // Reset stdout
          pyodide.runPython('sys.stdout = sys.__stdout__');

          // Return printed output
          return stdout || '(No output)';
        } catch (err) {
          return `Python Error: ${String(err)}`;
        }
      },
    },

    // 2. Pip command to install Python packages via micropip
    pip: {
      description: 'Install a python package. Usage: pip install <package>',
      usage: 'pip install <package>',
      fn: async (...args) => {
        if (args.length < 2 || args[0] !== 'install') {
          return 'Usage: pip install <package>';
        }
        const packageName = args[1];
        if (pyodideLoading || !pyodide) {
          return 'Pyodide is still loading. Please wait.';
        }
        try {
          await pyodide.runPythonAsync(`
import micropip
await micropip.install("${packageName}")
          `);
          return `Package "${packageName}" installed successfully.`;
        } catch (error) {
          return `Error installing "${packageName}": ${error}`;
        }
      },
    },

    // 3. List files
    list: {
      description: 'Lists all files in the project.',
      usage: 'list',
      fn: () => {
        if (files.length === 0) {
          return 'No files found.';
        }
        return files.map((f) => f.name).join('\n');
      },
    },
  };

  return (
    <Paper
      sx={{
        position: 'relative',
        top: 10,
        left: 0,
        width: '100%',
        height: '93vh',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
      }}
    >
      {/* Header bar with a Close button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#111',
          color: '#fff',
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="subtitle1">Browser Terminal (Python)</Typography>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ReactConsoleEmulator
          commands={commands}
          promptLabel={`${name}~`}
          style={{
            height: '100%',
            backgroundColor: '#000',
            color: '#0f0',
            fontFamily: 'monospace',
            overflow: 'auto',
            borderRadius: 0,
          }}
          autofocus
        />
      </Box>
    </Paper>
  );
}
